import {
  CreateOrg,
  CreateOrgReview,
  OrgMemberRole,
  SendOrgInvitationRequest,
  UpdateOrg
} from "@humanex/interfaces";
import { getErrorMessage, toast, useI18n } from "@humanex/ui/libs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";

import { orgApiClient } from "@/libs/api";

export const orgKeys = {
  all: ["orgs"] as const,
  listByUser: (userId: string) => [...orgKeys.all, "user", userId] as const,
  popular: () => [...orgKeys.all, "popular"] as const,
  pendingInvitations: (orgId: string) => [...orgKeys.all, orgId, "pending-invitations"] as const,
  activation: (orgId: string) => [...orgKeys.all, orgId, "activation"] as const
};

export function useOrgsByUserQuery(userId: string) {
  return useQuery({
    queryKey: orgKeys.listByUser(userId),
    queryFn: () => orgApiClient.getListByUserId(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000
  });
}

export function usePopularOrgsQuery() {
  return useQuery({
    queryKey: orgKeys.popular(),
    queryFn: () => orgApiClient.getListPopular(),
    staleTime: 10 * 60 * 1000
  });
}

export function useCreateOrg(
  options: {
    onSuccess?: (result: Awaited<ReturnType<typeof orgApiClient.create>>) => void;
  } = {}
) {
  const { t } = useI18n();
  return useMutation({
    mutationFn: (data: CreateOrg) => orgApiClient.create(data),
    onSuccess: (result) => {
      options.onSuccess?.(result);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useUpdateOrg(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: UpdateOrg }) => orgApiClient.update(orgId, data),
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useUpdateOrgMemberRole(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const router = useRouter();
  return useMutation({
    mutationFn: ({ orgId, userId, role }: { orgId: string; userId: string; role: OrgMemberRole }) =>
      orgApiClient.updateMemberRole(orgId, userId, role),
    onSuccess: () => {
      router.invalidate();
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useRemoveOrgMember(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const router = useRouter();
  return useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      orgApiClient.removeOrgMember(orgId, userId),
    onSuccess: () => {
      router.invalidate();
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useCreateOrgReview(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useMutation({
    mutationFn: ({ orgId, data }: { orgId: string; data: CreateOrgReview }) =>
      orgApiClient.createReview(orgId, data),
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useSendOrgInvitation(
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}
) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    onMutate: () => {
      console.log(20125117, "useSendOrgInvitation", "onMutate");
    },
    mutationFn: ({ orgId, data }: { orgId: string; data: SendOrgInvitationRequest }) => {
      console.log(20125117, "useSendOrgInvitation", orgId, data);
      return orgApiClient.sendInvitation(orgId, data);
    },
    onSuccess: (_data, variables) => {
      console.log(20125117, "useSendOrgInvitation", "onSuccess");
      queryClient.invalidateQueries({
        queryKey: orgKeys.pendingInvitations(variables.orgId)
      });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      console.log(20125117, "useSendOrgInvitation", "onError", error);
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
      options.onError?.(error);
    },
    onSettled: () => {
      console.log(20125117, "useSendOrgInvitation", "onSettled");
    }
  });
}

export function useOrgInvitationByToken(token: string) {
  return useQuery({
    queryKey: ["org-invitation", token],
    queryFn: () => orgApiClient.getOrgInvitation(token),
    enabled: !!token,
    staleTime: 60 * 1000
  });
}

export function useOrgPendingInvitationsQuery(orgId: string) {
  return useQuery({
    queryKey: orgKeys.pendingInvitations(orgId),
    queryFn: () => orgApiClient.getPendingInvitations(orgId),
    enabled: !!orgId,
    staleTime: 30 * 1000
  });
}

export function useAcceptOrgInvitation(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useMutation({
    mutationFn: (token: string) => orgApiClient.acceptOrgInvitation(token),
    onSuccess: () => {
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}

export function useOrgActivationQuery(orgId: string) {
  return useQuery({
    queryKey: orgKeys.activation(orgId),
    queryFn: () => orgApiClient.getActivationStatus(orgId),
    enabled: !!orgId,
    staleTime: 30 * 1000
  });
}

export function useActivateOrg(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, plan }: { orgId: string; plan: string }) => orgApiClient.activate(orgId, { plan }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: orgKeys.activation(variables.orgId) });
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
    }
  });
}
